// Non-blocking replacement for window.alert(): a small toast that stacks in the
// bottom-right corner and dismisses itself. Pure DOM — works from any component,
// hook or util without a provider.
type ToastKind = 'success' | 'error' | 'info';

const STYLES: Record<ToastKind, { bg: string; border: string }> = {
  success: { bg: '#065f46', border: '#10b981' },
  error: { bg: '#991b1b', border: '#ef4444' },
  info: { bg: '#1e3a8a', border: '#3b82f6' },
};

const inferKind = (message: string): ToastKind => {
  if (/^\s*(❌|⚠️|🚫)/.test(message) || /\b(fail(ed|ure)?|error|could not|cannot|can't|invalid|unable|not allowed|denied|required)\b/i.test(message)) {
    return 'error';
  }
  if (/^\s*(✅|🎉|🚨|⏪|📧|🔔)/.test(message) || /\b(success(fully)?|saved|sent|added|updated|deleted|removed|approved|created|copied|recorded)\b/i.test(message)) {
    return 'success';
  }
  return 'info';
};

const getContainer = (): HTMLElement => {
  let el = document.getElementById('app-toast-container');
  if (!el) {
    el = document.createElement('div');
    el.id = 'app-toast-container';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.style.cssText = 'position:fixed;right:16px;bottom:16px;z-index:100000;display:flex;flex-direction:column;gap:8px;max-width:min(420px,calc(100vw - 32px));pointer-events:none;';
    document.body.appendChild(el);
  }
  return el;
};

export function toast(message: unknown, kind?: ToastKind): void {
  const text = String(message ?? '');
  if (!text || typeof document === 'undefined') return;

  const style = STYLES[kind || inferKind(text)];
  const item = document.createElement('div');
  item.textContent = text;
  item.style.cssText =
    `background:${style.bg};color:#fff;border-left:4px solid ${style.border};padding:10px 14px;border-radius:8px;` +
    'font-size:14px;line-height:1.4;box-shadow:0 8px 24px rgba(0,0,0,.25);white-space:pre-line;pointer-events:auto;' +
    'cursor:pointer;opacity:0;transform:translateY(8px);transition:opacity .2s ease,transform .2s ease;';

  const remove = () => {
    item.style.opacity = '0';
    item.style.transform = 'translateY(8px)';
    setTimeout(() => item.remove(), 200);
  };
  item.addEventListener('click', remove);

  getContainer().appendChild(item);
  requestAnimationFrame(() => {
    item.style.opacity = '1';
    item.style.transform = 'translateY(0)';
  });
  // errors stay a little longer so they can be read
  setTimeout(remove, kind === 'error' || style === STYLES.error ? 6500 : 4000);
}
